import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import Breadcrumbs from '../../components/BreadCrumbs';
import StyledHeadingText from '../../components/Fonts/StyledHeadingText';
import Dropdown from '../../components/inputFields/DropDown';
import Button from '../../components/buttons/Button';
import CustomLoading from '../../components/customLoading/CustomLoading';
import { getDoctorCenters, getRevenueSummary, sendMonthlyStatsOtp } from '../../services/doctor/doctor_view_dashboard';
import { FiPackage } from 'react-icons/fi';
import { FaUserDoctor } from 'react-icons/fa6';
import { GiMedicinePills } from 'react-icons/gi';
import { HiOutlineOfficeBuilding } from 'react-icons/hi';
import { RxCalendar } from 'react-icons/rx';
import { BiRefresh } from 'react-icons/bi';
import { HiOutlineLockClosed } from 'react-icons/hi';
import { getUserRole } from '../../utils/localStoarege';

// Admins must verify an OTP sent to this number to view Monthly Stats; access lasts 30 minutes.
const STATS_OTP_NUMBER = "9301307667";
const STATS_UNLOCK_KEY = "monthly_stats_unlocked_at";
const STATS_UNLOCK_MS = 30 * 60 * 1000;

const getUnlockRemaining = () => {
    try {
        const unlockedAt = Number(sessionStorage.getItem(STATS_UNLOCK_KEY) || 0);
        return Math.max(0, unlockedAt + STATS_UNLOCK_MS - Date.now());
    } catch (error) {
        return 0;
    }
};

const MODE_ORDER = ["NEFT", "UPI", "CARD", "CASH", "OTHER"];

const MODE_COLORS = {
    NEFT: "#3d77a8",
    UPI: "#22a06b",
    CARD: "#a855f7",
    CASH: "#f59e0b",
    OTHER: "#94a3b8",
};

const formatINR = (value) => {
    const num = Number(value || 0);
    return `₹${num.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
};

const sumModes = (byMode = {}) =>
    MODE_ORDER.reduce((acc, mode) => acc + Number(byMode?.[mode] || 0), 0);

// Only current month and previous month are allowed.
const buildMonthOptions = () => {
    const now = new Date();
    const options = [];
    for (let i = 0; i < 2; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthName = d.toLocaleString("en-US", { month: "long" });
        options.push({
            label: `${monthName} ${d.getFullYear()}`,
            value: `${monthName}-${d.getFullYear()}`,
        });
    }
    return options;
};

const ModeSplitBar = ({ byMode = {} }) => {
    const total = sumModes(byMode) || 1;
    return (
        <div className="mt-3">
            <div className="flex h-2 w-full overflow-hidden rounded-full bg-gray-100">
                {MODE_ORDER.map((mode) => {
                    const pct = (Number(byMode?.[mode] || 0) / total) * 100;
                    if (pct <= 0) return null;
                    return (
                        <div
                            key={mode}
                            style={{ width: `${pct}%`, backgroundColor: MODE_COLORS[mode] }}
                            title={`${mode}: ${formatINR(byMode?.[mode])}`}
                        />
                    );
                })}
            </div>
            <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5">
                {MODE_ORDER.map((mode) => (
                    <div key={mode} className="flex items-center justify-between text-xs">
                        <span className="flex items-center gap-1.5 text-[#6B7280]">
                            <span
                                className="inline-block h-2 w-2 rounded-full"
                                style={{ backgroundColor: MODE_COLORS[mode] }}
                            />
                            {mode}
                        </span>
                        <span className="font-medium text-gray-700">{formatINR(byMode?.[mode])}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

const SectionCard = ({ title, icon, accent, total, byMode, grandTotal }) => {
    const share = grandTotal > 0 ? Math.round((Number(total || 0) / grandTotal) * 100) : 0;
    return (
        <div className="flex flex-col rounded-xl border border-gray-100 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
            <div className="flex items-start justify-between">
                <div className="flex items-center gap-2.5">
                    <span
                        className="flex h-9 w-9 items-center justify-center rounded-lg text-lg"
                        style={{ backgroundColor: `${accent}1a`, color: accent }}
                    >
                        {icon}
                    </span>
                    <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
                </div>
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[0.65rem] font-semibold text-gray-500">
                    {share}% of total
                </span>
            </div>
            <p className="mt-3 text-xl font-bold text-gray-900">{formatINR(total)}</p>
            <ModeSplitBar byMode={byMode} />
        </div>
    );
};

const MonthlyStatsOtpGate = ({ onVerified }) => {
    const [serverOtp, setServerOtp] = useState(null);
    const [otpInput, setOtpInput] = useState("");
    const [sendingOtp, setSendingOtp] = useState(false);

    const handleSendOtp = async () => {
        setSendingOtp(true);
        try {
            const formData = new FormData();
            formData.append("mobile_number", STATS_OTP_NUMBER);
            const response = await sendMonthlyStatsOtp(formData);
            if (response?.status === 200) {
                setServerOtp(String(response.data.otp));
                setOtpInput("");
                toast.success("OTP sent to +91 93013 07667");
            } else {
                toast.error("Unable to send OTP, please try again");
            }
        } catch (error) {
            const errors = error?.response?.data?.error || "Unable to send OTP, please try again";
            toast.error(errors);
        } finally {
            setSendingOtp(false);
        }
    };

    const handleVerifyOtp = () => {
        if (!otpInput) {
            toast.error("Enter the OTP");
            return;
        }
        if (otpInput.trim() !== serverOtp) {
            toast.error("Incorrect OTP");
            return;
        }
        try {
            sessionStorage.setItem(STATS_UNLOCK_KEY, String(Date.now()));
        } catch (error) {
            console.error("Unable to store unlock time:", error);
        }
        setServerOtp(null);
        setOtpInput("");
        toast.success("OTP verified");
        onVerified();
    };

    return (
        <div className="flex justify-center py-16">
            <div className="w-full max-w-sm rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
                <div className="flex items-center gap-2.5">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <HiOutlineLockClosed size={18} />
                    </span>
                    <StyledHeadingText text={"Monthly Stats Locked"} size={"text-base"} />
                </div>
                <p className="mt-3 text-xs text-gray-500">
                    An OTP will be sent to +91 93013 07667. Access stays open for 30 minutes after verification.
                </p>
                <Button
                    type="button"
                    onClick={handleSendOtp}
                    disabled={sendingOtp}
                    className="mt-4 w-full rounded-lg border border-primary bg-white py-1.5 text-sm text-primary"
                >
                    {sendingOtp ? "Sending..." : serverOtp ? "Resend OTP" : "Send OTP"}
                </Button>
                {serverOtp && (
                    <div className="mt-4 flex flex-col gap-2">
                        <input
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            value={otpInput}
                            onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, ""))}
                            onKeyDown={(e) => e.key === "Enter" && handleVerifyOtp()}
                            placeholder="Enter OTP"
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none"
                        />
                        <Button
                            type="button"
                            onClick={handleVerifyOtp}
                            className="w-full rounded-lg bg-primary py-1.5 text-sm text-white"
                        >
                            Verify & Open
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

function Finances() {
    const isAdmin = getUserRole() === "admin";
    const [unlocked, setUnlocked] = useState(() => !isAdmin || getUnlockRemaining() > 0);

    // Re-lock once the 30-minute window runs out, even if the page stays open.
    useEffect(() => {
        if (!isAdmin || !unlocked) return;
        const timer = setTimeout(() => {
            setUnlocked(false);
            toast.info("Monthly Stats session expired, verify OTP again");
        }, getUnlockRemaining());
        return () => clearTimeout(timer);
    }, [isAdmin, unlocked]);

    if (!unlocked) {
        return (
            <>
                <Breadcrumbs items={[
                    { name: 'Home', link: "/doctor/home" },
                    { name: 'Finances' },
                ]} />
                <MonthlyStatsOtpGate onVerified={() => setUnlocked(true)} />
            </>
        );
    }

    return <FinancesContent />;
}

function FinancesContent() {
    const monthOptions = useMemo(() => buildMonthOptions(), []);
    const [selectedMonth, setSelectedMonth] = useState(monthOptions[0].value);
    const [centers, setCenters] = useState([]);
    const [selectedCenter, setSelectedCenter] = useState("");
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);

    const fetchCenters = async () => {
        try {
            const res = await getDoctorCenters("?page_size=50");
            if (res.status === 200) {
                setCenters(res.data.results || []);
            } else {
                toast.error("Unable to fetch centers");
            }
        } catch (error) {
            console.error("Error fetching centers:", error);
        }
    };

    const fetchSummary = async (month, center) => {
        setLoading(true);
        try {
            const res = await getRevenueSummary({ month, center });
            if (res.status === 200) {
                setData(res.data);
            } else {
                toast.error("Unable to fetch revenue summary");
            }
        } catch (error) {
            const message = error?.response?.status === 404
                ? "Center not found"
                : "Unable to fetch revenue summary";
            toast.error(message);
            console.error("Error fetching revenue summary:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCenters();
    }, []);

    useEffect(() => {
        fetchSummary(selectedMonth, selectedCenter);
    }, [selectedMonth, selectedCenter]);

    const handleMonthChange = (value) => {
        setSelectedMonth(value || monthOptions[0].value);
    };

    const handleCenterChange = (value) => {
        setSelectedCenter(value || "");
    };

    const grandTotal = Number(data?.grand_total || 0);

    const packageTypes = data?.package?.by_package_type
        ? Object.entries(data.package.by_package_type)
        : [];

    return (
        <>
            <Breadcrumbs items={[
                { name: 'Home', link: "/doctor/home" },
                { name: 'Finances' },
            ]} />

            <div className="py-2">
                {/* Header + filter bar */}
                <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <StyledHeadingText text={"Monthly Revenue Summary"} size={"text-base"} />
                            <p className="mt-0.5 text-[0.7rem] text-gray-500">
                                Only the current and previous month are available.
                            </p>
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                            <div className="flex items-center rounded-lg border border-gray-200 pl-2">
                                <RxCalendar className="text-gray-400" size={15} />
                                <Dropdown
                                    placeholder={monthOptions.find((o) => o.value === selectedMonth)?.label || "Select Month"}
                                    options={monthOptions}
                                    onChange={handleMonthChange}
                                    className="text-black min-w-[9rem]"
                                    needSearch
                                />
                            </div>
                            <div className="flex items-center rounded-lg border border-gray-200 pl-2">
                                <HiOutlineOfficeBuilding className="text-gray-400" size={15} />
                                <Dropdown
                                    placeholder={"All Centers"}
                                    options={centers.map((center) => ({
                                        value: center.name,
                                        label: center.name,
                                    }))}
                                    onChange={handleCenterChange}
                                    className="text-black min-w-[9rem]"
                                    needSearch
                                />
                            </div>
                            <Button
                                children={
                                    <span className="flex items-center gap-1">
                                        <BiRefresh size={16} /> Refresh
                                    </span>
                                }
                                onClick={() => fetchSummary(selectedMonth, selectedCenter)}
                                className="bg-primary text-white rounded-lg text-xs"
                            />
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center items-center py-24">
                        <CustomLoading />
                    </div>
                ) : !data ? (
                    <p className="py-24 text-center text-sm text-gray-500">No data available.</p>
                ) : (
                    <>
                        {/* Grand total banner */}
                        <div className="mt-4 overflow-hidden rounded-xl bg-gradient-to-r from-primary to-secondary p-6 text-white shadow-sm">
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                                <div>
                                    <p className="text-xs font-medium uppercase tracking-wide text-white/70">
                                        Grand Total
                                    </p>
                                    <p className="mt-1 text-3xl font-bold">{formatINR(data.grand_total)}</p>
                                </div>
                                <div className="text-xs text-white/80 sm:text-right">
                                    <p className="flex items-center gap-1.5 sm:justify-end">
                                        <RxCalendar size={13} />
                                        {data?.filters?.month}
                                        {data?.filters?.start_date
                                            ? ` · ${data.filters.start_date} – ${data.filters.end_date}`
                                            : ""}
                                    </p>
                                    <p className="mt-1 flex items-center gap-1.5 sm:justify-end">
                                        <HiOutlineOfficeBuilding size={13} />
                                        {data?.filters?.center || "All Centers"}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Section cards */}
                        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                            <SectionCard
                                title="Package"
                                icon={<FiPackage />}
                                accent="#a855f7"
                                total={data?.package?.total}
                                byMode={data?.package?.by_mode}
                                grandTotal={grandTotal}
                            />
                            <SectionCard
                                title="Consultation"
                                icon={<FaUserDoctor />}
                                accent="#3d77a8"
                                total={data?.consultation?.total}
                                byMode={data?.consultation?.by_mode}
                                grandTotal={grandTotal}
                            />
                            <SectionCard
                                title="Medicine"
                                icon={<GiMedicinePills />}
                                accent="#ef4444"
                                total={data?.medicine?.total}
                                byMode={data?.medicine?.by_mode}
                                grandTotal={grandTotal}
                            />
                        </div>

                        {/* Package type breakdown */}
                        {packageTypes.length > 0 && (
                            <div className="mt-4 rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
                                <StyledHeadingText text={"Package Revenue by Type"} size={"text-sm"} />
                                <div className="mt-3 overflow-x-auto">
                                    <table className="w-full min-w-[42rem] text-xs">
                                        <thead>
                                            <tr className="border-b border-gray-200 text-left text-[#6B7280]">
                                                <th className="py-2 font-semibold">Package Type</th>
                                                <th className="py-2 text-right font-semibold">Total</th>
                                                {MODE_ORDER.map((mode) => (
                                                    <th key={mode} className="py-2 text-right font-semibold">
                                                        {mode}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {packageTypes.map(([type, detail], idx) => (
                                                <tr
                                                    key={type}
                                                    className={`border-b border-gray-100 last:border-b-0 ${idx % 2 ? "bg-gray-50/60" : ""} hover:bg-primary/5`}
                                                >
                                                    <td className="py-2.5 font-medium text-gray-800">{type}</td>
                                                    <td className="py-2.5 text-right font-semibold text-gray-900">
                                                        {formatINR(detail?.total)}
                                                    </td>
                                                    {MODE_ORDER.map((mode) => {
                                                        const val = Number(detail?.by_mode?.[mode] || 0);
                                                        return (
                                                            <td
                                                                key={mode}
                                                                className={`py-2.5 text-right ${val ? "text-gray-700" : "text-gray-300"}`}
                                                            >
                                                                {formatINR(val)}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    );
}

export default Finances;
